import { motion } from 'framer-motion'

export default function ResultScreen({ result, onContinue, onRetry }) {
  const getRank = (accuracy) => {
    if (accuracy >= 95) return { label: 'S', color: '#FFD700', emoji: '🌟', gradient: 'from-yellow-400 via-pink-400 to-purple-400' }
    if (accuracy >= 85) return { label: 'A', color: '#DA70D6', emoji: '⚡', gradient: 'from-pink-400 via-purple-400 to-blue-400' }
    if (accuracy >= 75) return { label: 'B', color: '#00CED1', emoji: '💎', gradient: 'from-cyan-400 via-blue-400 to-purple-400' }
    if (accuracy >= 60) return { label: 'C', color: '#FF69B4', emoji: '🎯', gradient: 'from-pink-300 via-purple-300 to-blue-300' }
    return { label: 'D', color: '#98FB98', emoji: '💫', gradient: 'from-green-300 via-cyan-300 to-blue-300' }
  }

  const rank = getRank(result.accuracy)

  return (
    <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 overflow-auto flex items-center justify-center relative">
      {/* キラキラ背景装飾 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-2xl"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0,
              scale: 0
            }}
            animate={{
              y: [null, Math.random() * window.innerHeight],
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
              rotate: [0, 360]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          >
            {['✨', '⭐', '💫', '🌟', '💖'][Math.floor(Math.random() * 5)]}
          </motion.div>
        ))}
      </div>

      <div className="max-w-2xl w-full px-6 py-8 relative z-10">
        {/* Rank Display */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', duration: 0.8 }}
          className="text-center mb-8"
        >
          <motion.div
            className="text-8xl mb-4"
            animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {rank.emoji}
          </motion.div>
          <motion.div
            className={`font-display text-9xl font-black mb-4 bg-gradient-to-r ${rank.gradient} bg-clip-text text-transparent`}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            {rank.label}
          </motion.div>
          <div className="text-2xl text-purple-600 font-bold">✨ ランク ✨</div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.3, type: "spring" }}
          className="card-neon mb-8"
        >
          <div className="grid grid-cols-2 gap-6">
            {/* Score */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="text-purple-600 text-sm mb-2 font-bold">スコア</div>
              <div className="font-display text-4xl font-black bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                💎 {result.score.toLocaleString()}
              </div>
            </motion.div>

            {/* Max Combo */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="text-purple-600 text-sm mb-2 font-bold">最大コンボ</div>
              <div className="font-display text-4xl font-black bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                🔥 {result.combo}
              </div>
            </motion.div>

            {/* Accuracy */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="text-purple-600 text-sm mb-2 font-bold">正答率</div>
              <div className="font-display text-4xl font-black bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                {result.accuracy}%
              </div>
            </motion.div>

            {/* Time */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
            >
              <div className="text-purple-600 text-sm mb-2 font-bold">時間</div>
              <div className="font-display text-4xl font-black bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                {Math.floor(result.duration / 60)}:{(result.duration % 60).toString().padStart(2, '0')}
              </div>
            </motion.div>
          </div>

          <div className="mt-6 pt-6 border-t-2 border-purple-300">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-purple-600 font-bold">正解数</span>
              <span className="text-green-600 font-display font-black text-lg">✓ {result.correctAnswers}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-purple-600 font-bold">不正解数</span>
              <span className="text-red-500 font-display font-black text-lg">✗ {result.incorrectAnswers}</span>
            </div>
          </div>
        </motion.div>

        {/* Unlocks (if any) */}
        {result.unlocked && result.unlocked.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, type: "spring" }}
            className="card-neon mb-8"
          >
            <div className="text-center">
              <motion.div
                className="text-4xl mb-2"
                animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                🎁
              </motion.div>
              <div className="text-purple-700 font-display text-xl font-black mb-4">
                ✨ 新しいアイテムをアンロック！ ✨
              </div>
              <div className="space-y-2">
                {result.unlocked.map((item, index) => (
                  <motion.div
                    key={index}
                    className="text-purple-600 font-bold bg-gradient-to-r from-pink-200 to-purple-200 px-4 py-2 rounded-xl"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                  >
                    {item}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex gap-4"
        >
          <motion.button
            onClick={onRetry}
            className="flex-1 btn-neon bg-gradient-to-r from-orange-400 to-pink-400"
            whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
            whileTap={{ scale: 0.95 }}
          >
            🔄 リトライ
          </motion.button>
          <motion.button
            onClick={onContinue}
            className="flex-1 btn-neon bg-gradient-to-r from-blue-400 to-purple-400"
            whileHover={{ scale: 1.05, rotate: [0, 5, -5, 0] }}
            whileTap={{ scale: 0.95 }}
          >
            ➡️ 続ける
          </motion.button>
        </motion.div>

        {/* Motivational Message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-8 text-purple-600 text-base font-black"
        >
          {rank.label === 'S' && "🎉 パーフェクト！磨き抜かれた語感だね！✨"}
          {rank.label === 'A' && "⚡ 素晴らしい！その調子で頑張ろう！💖"}
          {rank.label === 'B' && "💎 よくできた！上達してきているぞ！🌟"}
          {rank.label === 'C' && "🎯 悪くない！練習あるのみ！💪"}
          {rank.label === 'D' && "💫 ここからが腕の見せどころ！一歩ずつ積み重ねよう！🌈"}
        </motion.div>
      </div>
    </div>
  )
}
